/**
 * Portfolio Overview — the tier model, and what it costs in people.
 *
 * The thresholds and hours targets are LIVE inputs: moving one re-tiers the
 * whole book in the browser and re-renders the counts, hours and FTE figures
 * immediately. That is possible because the engine recomputes tiering from live
 * spend on every call and holds no stored tier per account — the property that
 * makes a slider honest rather than a guess at what a save would do.
 *
 * Nothing here writes. Saving a modified model is an explicit, separate action
 * (POST /api/staffing/config creates a new immutable version), so a manager can
 * explore freely without leaving a trail of half-considered versions at the head
 * of the audit history.
 *
 * FINANCIALS ARE SERVER-GATED. The cost column appears only when cm-api actually
 * sent the figures — `financialsRedacted` — rather than when the client believes
 * the user holds view:staffing_financials. The client's belief is an affordance;
 * the server's omission is the boundary.
 */
import { Fragment, useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Kpi,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@andavo/web-ui';
import {
  DetailRow,
  DisclosureCell,
  DisclosureHeaderCell,
  detailRowId,
  totalColumns,
  useExpandedRows,
  zebraFor,
} from '@/features/crm/components/ExpandableRow';
import { useMe } from '@/features/crm/auth/use-capabilities';
import { useStaffingData } from '../../StaffingDataProvider';
import {
  canPublishConfig,
  publishConfig,
  StaffingWriteError,
  toPublishInput,
} from '../../mutations';
import type { StoredTierDto } from '../../api';
import type { Account } from '@andavo/staffing-engine';
import { buildStaffingModel, StaffingModelError } from '../../lib/model';
import { dollars, ftes, hours, tierFloor, volumeM } from '../../lib/format';
import {
  addNamedTier,
  addTierAbove,
  addTierBelow,
  isNamed,
  removeTier,
  tierProblems,
} from '../../lib/tiers';
import { DiagnosticsPanel } from '../../components/DiagnosticsPanel';
import { SnapshotPanel } from '../../components/SnapshotPanel';
import { StaffingScreenState } from '../../components/StaffingScreenState';

/**
 * Data columns in the tier table, which varies with what this reader may see and
 * do. Counted rather than hardcoded because the detail row's colSpan has to
 * match, and a wrong colSpan is a silently misaligned table rather than an error.
 */
const DATA_COLUMNS = (canEdit: boolean, showFinancials: boolean) =>
  7 + (canEdit ? 1 : 0) + (showFinancials ? 1 : 0);

export default function StaffingOverviewPage() {
  const {
    read,
    loading,
    error,
    includeInactive,
    setIncludeInactive,
    reload,
    scenario,
    setScenario,
    resetScenario,
    scenarioChanges,
  } = useStaffingData();
  const { can } = useMe();
  const rows = useExpandedRows();
  // Tier edits live in the SHARED scenario, not in this page. Reshaping the
  // bands is a question about cost (here) and about who ends up with whom
  // (Portfolios), and page-local state reset the moment you went to look at the
  // other half of your own answer.
  const draftTiers = scenario.tiers ?? null;
  const draftBalanceBy = scenario.balanceBy ?? null;
  const setDraftTiers = (next: StoredTierDto[]) =>
    setScenario((prev) => ({ ...prev, tiers: next }));
  const setDraftBalanceBy = (next: 'count' | 'spend') =>
    setScenario((prev) => ({ ...prev, balanceBy: next }));
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<StaffingWriteError | null>(
    null,
  );
  const [published, setPublished] = useState<number | null>(null);

  const savedTiers = read?.config.config?.tiers ?? null;
  const tiers = draftTiers ?? savedTiers;
  const savedBalanceBy = read?.config.config?.balanceBy ?? 'count';
  const balanceBy = draftBalanceBy ?? savedBalanceBy;

  const model = useMemo(() => {
    if (!read || !tiers) return null;
    try {
      return {
        ok: true as const,
        value: buildStaffingModel(read, {
          tiers,
          balanceBy,
          // The slot and pin halves of the same overlay. Without them this page
          // would price a tier model that the other tab is not showing.
          slots: scenario.slots,
          pins: scenario.pins,
          // And the two that change the SIZE of the team. `hires` was missing
          // here from the day it shipped: Portfolios would show a scenario with
          // a new CSM in Medium while this page went on reporting the FTEs and
          // the cost of the team without them — the same "two tabs disagree"
          // failure the comment above exists to prevent, one field along.
          hires: scenario.hires,
          departures: scenario.departures,
        }),
      };
    } catch (err) {
      return {
        ok: false as const,
        message:
          err instanceof StaffingModelError
            ? err.message
            : 'Could not compute the staffing model.',
      };
    }
  }, [read, tiers, balanceBy, scenario.slots, scenario.pins]);

  // A MODEL ERROR IS NOT A DEAD END. The engine refuses an out-of-order tier
  // list, which is a state you pass THROUGH while editing one — type a floor,
  // and for a keystroke it sits above the band it belongs under. The page used
  // to replace itself with the error card at that moment, taking the editor and
  // the Reset button with it, so the only way back was a reload. Now the editor
  // stays and the error is rendered beside it, next to the field that caused it.
  const modelError = model && !model.ok ? model.message : null;
  if (!read || !tiers) {
    return (
      <StaffingScreenState
        configured={read?.config.configured ?? false}
        error={error}
        loading={loading}
        modelError={modelError}
        onResetScenario={resetScenario}
        scenarioChanges={scenarioChanges}
      />
    );
  }

  const m = model?.ok ? model.value : null;
  const totalVolume = m
    ? m.tierLoads.reduce((s, t) => s + t.bookingVolumeM, 0)
    : 0;
  const totalHours = m
    ? m.tierLoads.reduce((s, t) => s + t.monthlyHours, 0)
    : 0;
  const totalFtes = m ? m.tierLoads.reduce((s, t) => s + t.ftesNeeded, 0) : 0;
  const problems = tierProblems(tiers);
  const showFinancials = read.config.config?.financialsRedacted === false;
  const dirty = scenarioChanges > 0;
  // Editing the tier STACK is a manager action, same authority as publishing it.
  // A reader without it still sees every number; they just cannot reshape the
  // bands the numbers are computed from.
  const canEdit = can('manage:staffing');
  const publishable = canPublishConfig(
    read.config.config,
    can('manage:staffing'),
  );

  // accountId → who ends up with it. "Who is in Enterprise" leads straight to
  // "and who has them", and the answer is otherwise two tabs away.
  const ownerOf = new Map<string, string>();
  if (m) {
    for (const portfolio of m.result.portfolios) {
      const who = m.displayName(portfolio.csm);
      for (const a of [...portfolio.assigned, ...portfolio.pinned]) {
        ownerOf.set(a.id, who);
      }
    }
  }

  const reset = () => {
    resetScenario();
    setPublishError(null);
    setPublished(null);
  };

  const publish = async () => {
    if (!read.config.config) return;
    // Belt and braces with the disabled button: a version is immutable, so a
    // publish that slipped through with a known-bad tier list would sit at the
    // head of the audit trail permanently.
    if (tierProblems(tiers).length > 0) return;
    setPublishing(true);
    setPublishError(null);
    setPublished(null);
    try {
      // A version is INSERTED, never updated — the previous one stays readable,
      // which is what makes this an audit trail rather than a setting.
      const { version } = await publishConfig(
        toPublishInput(read.config.config, {
          tiers: tiers as StoredTierDto[],
          balanceBy,
        }),
      );
      setPublished(version);
      // Only the tier half is now saved. Slot and pin moves are still a
      // scenario — publishing a config does not write them — so clearing the
      // whole overlay here would silently discard them.
      setScenario((prev) => ({
        ...prev,
        tiers: undefined,
        balanceBy: undefined,
      }));
      // The saved model changed, so every number on the page is now stale.
      reload();
    } catch (err) {
      setPublishError(
        err instanceof StaffingWriteError
          ? err
          : new StaffingWriteError('Could not publish.', 0),
      );
    } finally {
      setPublishing(false);
    }
  };

  const patchTier = (index: number, patch: Partial<StoredTierDto>) => {
    setDraftTiers(
      (tiers as StoredTierDto[]).map((t, i) =>
        i === index ? { ...t, ...patch } : t,
      ),
    );
  };

  const tierLabel = (key: string) =>
    tiers.find((t) => t.key === key)?.label ?? key;

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi
          label="Customers in book"
          value={m ? String(m.accounts.length) : '—'}
        />
        <Kpi
          label="Booking volume (T12M)"
          value={m ? volumeM(totalVolume) : '—'}
        />
        <Kpi
          label="Service hours / month"
          value={m ? hours(totalHours) : '—'}
        />
        <Kpi label="FTEs needed" value={m ? ftes(totalFtes) : '—'} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Service tiers</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <p className="text-content-secondary text-sm">
            Thresholds are trailing-12-month <strong>booking volume</strong> —
            what the customer books, not what Andavo earns. Changes here are
            previewed live and are not saved until you publish a new version.
          </p>

          {canEdit ? (
            <div>
              {/* The one position no row's "below" can reach. This is the
                  strategic-band case: a tier ABOVE the current ceiling, asking
                  for more hours per customer. */}
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => setDraftTiers(addTierAbove(tiers, 0))}
                  size="sm"
                  variant="secondary"
                >
                  Add a band at the top
                </Button>
                {/* A DIFFERENT KIND, not another band. Strategic accounts are not
                  a size — they come in every size — so no threshold describes
                  them, and a band would re-tier them every time bookings moved.
                  Customers go in one at a time, from the Portfolios tab. */}
                <Button
                  onClick={() => setDraftTiers(addNamedTier(tiers))}
                  size="sm"
                  variant="secondary"
                >
                  Add a hand-picked tier
                </Button>
              </div>
            </div>
          ) : null}

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <DisclosureHeaderCell />
                  <TableHead>Tier</TableHead>
                  <TableHead>Floor ($M)</TableHead>
                  <TableHead>Hrs / customer / mo</TableHead>
                  {canEdit ? <TableHead>Band</TableHead> : null}
                  <TableHead className="text-right">Customers</TableHead>
                  <TableHead className="text-right">Booking volume</TableHead>
                  <TableHead className="text-right">Hours / mo</TableHead>
                  <TableHead className="text-right">FTEs</TableHead>
                  {showFinancials ? (
                    <TableHead className="text-right">Staffing cost</TableHead>
                  ) : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {tiers.map((tier, index) => {
                  const load = m?.tierLoads.find((t) => t.tier === tier.key);
                  const isHandPicked = isNamed(tier);
                  // The catch-all is the last BANDED tier — a hand-picked tier
                  // also has a null floor and is emphatically not it.
                  const isCatchAll = !isHandPicked && tier.minSpendM === null;
                  const cost =
                    tier.loadedCostPerFte != null && load
                      ? tier.loadedCostPerFte * load.ftesNeeded
                      : null;
                  const rowId = detailRowId('staffing-tier', tier.key);
                  const open = rows.isExpanded(tier.key);
                  const zebra = zebraFor(index);
                  return (
                    // The fragment keys the row and its detail as ONE record;
                    // keying the children re-keys the pair on every expand.
                    <Fragment key={tier.key}>
                      <TableRow data-zebra={zebra}>
                        <DisclosureCell
                          controls={rowId}
                          label={tier.label}
                          onToggle={() => rows.toggle(tier.key)}
                          open={open}
                        />
                        <TableCell className="font-medium">
                          {canEdit ? (
                            <input
                              aria-label={`Name of tier ${index + 1}`}
                              className="w-28 rounded border border-border-medium bg-surface-background px-2 py-1 text-sm"
                              onChange={(e) =>
                                patchTier(index, { label: e.target.value })
                              }
                              type="text"
                              value={tier.label}
                            />
                          ) : (
                            tier.label
                          )}
                        </TableCell>
                        <TableCell>
                          {isHandPicked ? (
                            // NOT the catch-all, despite also having no floor.
                            // The two read the same in the data and mean opposite
                            // things — "everything below" versus "nothing at all"
                            // — so they must never render the same on screen.
                            <span
                              className="text-content-secondary text-sm"
                              title="Customers go into this tier one at a time, from the Portfolios tab. Nothing lands here by size."
                            >
                              hand-picked
                            </span>
                          ) : isCatchAll ? (
                            // The catch-all has no editable floor ON PURPOSE:
                            // giving it one silently drops every negative-spend
                            // account (credits, adjustments) out of the book.
                            <span
                              className="text-content-secondary text-sm"
                              title="The lowest tier catches everything below, including zero and negative booking volume from credits and adjustments."
                            >
                              {tierFloor(null)}
                            </span>
                          ) : (
                            <input
                              aria-label={`${tier.label} floor in $M`}
                              className="w-16 rounded border border-border-medium bg-surface-background px-2 py-1 text-sm"
                              min={0}
                              onChange={(e) =>
                                patchTier(index, {
                                  minSpendM: Number(e.target.value),
                                })
                              }
                              step="0.1"
                              type="number"
                              value={tier.minSpendM ?? 0}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <input
                            aria-label={`${tier.label} hours per customer per month`}
                            className="w-16 rounded border border-border-medium bg-surface-background px-2 py-1 text-sm"
                            min={0}
                            onChange={(e) =>
                              patchTier(index, {
                                hoursPerAccountPerMonth: Number(e.target.value),
                              })
                            }
                            step="0.5"
                            type="number"
                            value={tier.hoursPerAccountPerMonth}
                          />
                        </TableCell>
                        {canEdit ? (
                          <TableCell>
                            <div className="flex w-40 gap-1">
                              {/* Only "below" per row, plus one "at the top"
                                control above the table. Every position is
                                reachable — above tier N IS below tier N-1 — and
                                a third button per row forced the column to wrap
                                three deep, which made each row 141px tall and
                                pushed the tier NAME out of view whenever the
                                table scrolled to reach these. */}
                              <Button
                                aria-label={`Add a band below ${tier.label}`}
                                onClick={() =>
                                  setDraftTiers(addTierBelow(tiers, index))
                                }
                                size="sm"
                                variant="ghost"
                              >
                                Below
                              </Button>
                              <Button
                                aria-label={`Remove ${tier.label}`}
                                disabled={tiers.length <= 1}
                                onClick={() =>
                                  setDraftTiers(removeTier(tiers, index))
                                }
                                size="sm"
                                variant="ghost"
                              >
                                Remove
                              </Button>
                            </div>
                          </TableCell>
                        ) : null}
                        <TableCell className="text-right tabular-nums">
                          {m ? (load?.accounts ?? 0) : '—'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {m ? volumeM(load?.bookingVolumeM ?? 0) : '—'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {m ? hours(load?.monthlyHours ?? 0) : '—'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {m ? ftes(load?.ftesNeeded ?? 0) : '—'}
                        </TableCell>
                        {showFinancials ? (
                          <TableCell className="text-right tabular-nums">
                            {cost === null || !m ? '—' : dollars(cost)}
                          </TableCell>
                        ) : null}
                      </TableRow>
                      {open && m ? (
                        <DetailRow
                          colSpan={totalColumns(
                            DATA_COLUMNS(canEdit, showFinancials),
                          )}
                          id={rowId}
                          testId={`staffing-tier-customers-${tier.key}`}
                          zebra={zebra}
                        >
                          <TierCustomers
                            accounts={m.result.byTier[tier.key] ?? []}
                            ownerOf={ownerOf}
                            tierLabel={tier.label}
                            unstaffed={
                              (m.result.unallocated[tier.key] ?? []).length > 0
                            }
                          />
                        </DetailRow>
                      ) : null}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col gap-3">
            <label className="flex items-center gap-2 text-sm">
              <span className="text-content-secondary">
                Balance each tier by
              </span>
              <select
                aria-label="Balance each tier by"
                className="rounded border border-border-medium bg-surface-background px-2 py-1 text-sm"
                onChange={(e) =>
                  setDraftBalanceBy(e.target.value as 'count' | 'spend')
                }
                value={balanceBy}
              >
                <option value="count">customer count</option>
                <option value="spend">booking volume</option>
              </select>
              <span className="text-content-secondary text-xs">
                {/* Worth saying on screen: within a tier these are different
                    jobs, and a tier staffed by one CSM is not balanced either
                    way because there is nothing to balance. */}
                Twelve $6M customers is not the same job as twelve $40M ones.
              </span>
            </label>

            {/* The tier list's own rules, checked here and reported inline.
                These MIRROR cm-api's validator rather than replacing it: the
                server still refuses a bad version, but discovering an
                out-of-order floor by pressing Publish and reading a 400 is a
                worse way to learn it than seeing it under the field. */}
            {problems.length > 0 ? (
              <div
                className="flex flex-col gap-1"
                data-testid="staffing-tier-problems"
                role="alert"
              >
                {problems.map((problem) => (
                  <p className="text-content-danger text-sm" key={problem}>
                    {problem}
                  </p>
                ))}
              </div>
            ) : null}

            {/* Said out loud, because it is the one edit that silently changes
                what the tool is FOR: a band with no seats holds real customers
                that no portfolio covers. The engine reports it, the diagnostics
                panel renders it, and this is the moment it becomes true. */}
            {m && m.diagnostics.unstaffedTiers.length > 0 && dirty ? (
              <p className="text-content-warning text-sm">
                {m.diagnostics.unstaffedTiers
                  .map(
                    (t) =>
                      `${tierLabel(t.tierKey)} holds ${t.accounts} customers and has no one staffing it`,
                  )
                  .join('; ')}
                . Add seats on the Portfolios tab, or those customers sit in no
                book at all.
              </p>
            ) : null}

            <div className="flex items-center gap-3">
              {dirty ? (
                <>
                  <span className="text-content-warning text-sm">
                    Previewing unsaved changes.
                  </span>
                  <Button
                    disabled={
                      publishing ||
                      !publishable.allowed ||
                      problems.length > 0 ||
                      !m
                    }
                    onClick={publish}
                  >
                    {publishing ? 'Publishing…' : 'Publish as a new version'}
                  </Button>
                  <Button onClick={reset} variant="secondary">
                    Reset to saved model
                  </Button>
                </>
              ) : (
                <span className="text-content-secondary text-sm">
                  Showing the saved model (version {read.config.config?.version}
                  ).
                </span>
              )}
            </div>

            {/* Why the button is disabled, rather than a control that always
                fails. Publishing needs view:staffing_financials as well as
                manage:staffing, because a config carries the cost model and a
                redacted read omits it. */}
            {dirty && !publishable.allowed ? (
              <p className="text-content-secondary text-sm">
                {publishable.reason}
              </p>
            ) : null}

            {modelError ? (
              <p className="text-content-danger text-sm" role="alert">
                {modelError}
              </p>
            ) : null}

            {publishError ? (
              <div className="flex flex-col gap-1" role="alert">
                <p className="text-content-danger text-sm">
                  {publishError.message}
                </p>
                {/* cm-api returns EVERY problem at once, deliberately: a version
                    is immutable, so someone fixing one should not discover the
                    rest a round-trip at a time. */}
                {publishError.problems.length > 0 ? (
                  <ul className="flex flex-col gap-0.5">
                    {publishError.problems.map((problem) => (
                      <li className="text-content-danger text-xs" key={problem}>
                        {problem}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}

            {published !== null ? (
              <p className="text-content-success text-sm" role="status">
                Published version {published}. The previous version is unchanged
                and still readable.
              </p>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {m ? (
        <DiagnosticsPanel
          diagnostics={m.diagnostics}
          displayName={m.displayName}
          tierLabel={tierLabel}
        />
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Book coverage</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 text-sm">
          <p className="text-content-secondary">
            {m
              ? `${m.result.coverage.assigned} of ${m.result.coverage.total} customers landed in exactly one portfolio.`
              : 'Coverage cannot be computed until the tier list above is valid.'}
          </p>
          <label className="flex items-center gap-2">
            <input
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              type="checkbox"
            />
            <span>
              Include inactive customers ({read.accounts.meta.totalRows} rows in
              the CRM, {read.accounts.meta.returned} in this book)
            </span>
          </label>
          <p className="text-content-secondary text-xs">
            Which subset the staffing book should cover is still unsettled — the
            CRM reconciliation has not run — so this is a choice you make rather
            than a default you inherit.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <SnapshotPanel
            includeInactive={includeInactive}
            model={m}
            read={read}
            scenarioChanges={scenarioChanges}
          />
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Who is actually in this tier.
 *
 * Sorted by booking volume DESCENDING, which is a different order from the
 * change lists on Portfolios (engine order, so the pair of columns reconcile).
 * The question this list answers is "who is in Enterprise" — and the useful end
 * of that answer is the top, not whichever customer the balancer happened to
 * place first.
 *
 * Capped with an explicit remainder, for the same reason the change lists are: a
 * list that stops at 25 and says nothing reads as a complete tier of 25, and the
 * catch-all really does hold 134 customers.
 */
function TierCustomers({
  accounts,
  ownerOf,
  tierLabel,
  unstaffed,
}: {
  accounts: readonly Account[];
  ownerOf: ReadonlyMap<string, string>;
  tierLabel: string;
  unstaffed: boolean;
}) {
  const ordered = [...accounts].sort(
    (a, b) => b.spendM - a.spendM || a.name.localeCompare(b.name),
  );
  const shown = ordered.slice(0, 25);

  if (accounts.length === 0) {
    return (
      <div className="detail-panel">
        <p className="text-content-secondary text-sm">
          No customers fall in {tierLabel}.
        </p>
      </div>
    );
  }

  return (
    <div className="detail-panel flex flex-col gap-2">
      {unstaffed ? (
        // Said HERE as well as in the diagnostics panel, because this is the
        // moment someone is looking at the list and would otherwise assume the
        // names in front of them are covered.
        <p className="text-content-warning text-sm">
          No one staffs {tierLabel}, so none of these customers is in a
          portfolio — they are excluded from every per-CSM total on this page.
        </p>
      ) : null}
      <table className="w-full text-sm">
        <thead>
          <tr className="text-content-secondary text-xs uppercase tracking-wide">
            <th className="text-left font-medium">Customer</th>
            <th className="text-right font-medium">Booking volume</th>
            <th className="text-left font-medium">Ends up with</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((a) => (
            <tr key={a.id}>
              <td>{a.name}</td>
              <td className="text-right tabular-nums">{volumeM(a.spendM)}</td>
              <td className="text-content-secondary">
                {/* An account in a tier nobody staffs has no owner, and that is
                    a finding rather than a blank. */}
                {ownerOf.get(a.id) ?? '— no one'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {ordered.length > shown.length ? (
        <p className="text-content-secondary text-xs italic">
          and {ordered.length - shown.length} more, smaller than{' '}
          {volumeM(shown[shown.length - 1]!.spendM)}
        </p>
      ) : null}
    </div>
  );
}
